import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, FolderKanban, Trash2, Edit3, Sparkles, ArrowUpRight, X } from "lucide-react";
import { useProjects } from "../lib/projects";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

export default function ProjectsPage() {
  const { projects, current, setCurrent, create, remove } = useProjects();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    industry: "",
    stage: "Idea",
    problem: "",
    solution: "",
    target_audience: "",
    business_model: "",
    revenue_model: "",
    description: "",
    goals: [],
    website: "",
    visibility: "Private",
    tags: [],
    team_size: "Solo Founder",
    icon: "🚀"
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Project Name is required.";
    if (!form.industry) newErrors.industry = "Industry is required.";
    if (!form.problem.trim()) newErrors.problem = "Problem Statement is required.";
    if (!form.solution.trim()) newErrors.solution = "Solution is required.";
    if (!form.description.trim()) newErrors.description = "Project Description is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setBusy(true);
    try {
      await create(form);
      toast.success("Project created");
      setOpen(false);
      setForm({
        name: "",
        tagline: "",
        industry: "",
        stage: "Idea",
        problem: "",
        solution: "",
        target_audience: "",
        business_model: "",
        revenue_model: "",
        description: "",
        goals: [],
        website: "",
        visibility: "Private",
        tags: [],
        team_size: "Solo Founder",
        icon: "🚀"
      });
      setErrors({});
    } catch (e) {
      toast.error("Could not create project");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? All generations will be lost.`)) return;
    try {
      await remove(p.project_id);
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Could not delete project");
    }
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">Workspace</p>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tighter">Projects</h1>
          <p className="mt-2 text-gray-600">All your startups in one place.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="glow-button rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
          data-testid="new-project-btn"
        >
          <Plus className="w-4 h-4" /> New project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="mt-12 rounded-[24px] border border-dashed border-gray-200 p-12 text-center bg-white">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center">
            <Sparkles className="w-7 h-7" />
          </div>
          <p className="mt-4 font-display text-2xl font-semibold">No Projects Yet</p>
          <p className="mt-2 text-gray-500 max-w-md mx-auto">Create your first startup project to begin using STUDLYF AI.</p>
          <button onClick={() => setOpen(true)} className="mt-6 glow-button rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2" data-testid="empty-new-project">
            <Plus className="w-4 h-4" /> Create New Project
          </button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => {
            const isActive = current?.project_id === p.project_id;
            return (
              <motion.div
                key={p.project_id}
                whileHover={{ y: -4 }}
                onClick={() => setCurrent(p)}
                className={`relative rounded-[24px] bg-white border p-5 cursor-pointer transition-all ${
                  isActive ? "border-[#7C3AED]/40 shadow-[0_14px_40px_rgba(108,99,255,0.12)]" : "border-gray-100 hover:shadow-[0_12px_36px_rgba(0,0,0,0.06)]"
                }`}
                data-testid={`project-card-${p.project_id}`}
              >
                {p.is_demo && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white">Demo</span>
                )}
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center text-lg font-semibold shadow-sm">
                  {p.icon || <FolderKanban className="w-5 h-5" />}
                </div>
                <p className="mt-4 font-display text-lg font-semibold">{p.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{p.industry || "—"} · {p.stage}</p>
                <p className="mt-3 text-sm text-gray-700 line-clamp-2">{p.tagline || "No tagline yet."}</p>

                <div className="mt-4 flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${isActive ? "text-[#7C3AED]" : "text-gray-500"}`}>
                    {isActive ? "Selected" : "Click to select"} <ArrowUpRight className="w-3 h-3" />
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500"
                    data-testid={`delete-project-${p.project_id}`}
                    aria-label="Delete project"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-[24px] p-0 overflow-hidden" data-testid="new-project-dialog">
          <div className="px-7 pt-7 pb-2">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-2xl tracking-tight">New project</DialogTitle>
              <DialogDescription>Tell us a little about what you&apos;re building.</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={submit} className="px-7 pb-7 flex flex-col">
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 mb-4 scrollbar-thin">
              {/* Project Icon */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-2">Project Icon</label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center text-xl shrink-0 font-semibold shadow-sm">
                    {form.icon || "🚀"}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {["🚀", "💡", "💻", "📈", "🎨", "🧪", "🌍"].map((ico) => (
                      <button
                        key={ico}
                        type="button"
                        onClick={() => setForm({ ...form, icon: ico })}
                        className={`w-8 h-8 rounded-lg border text-sm flex items-center justify-center transition ${
                          form.icon === ico ? "border-[#7C3AED] bg-purple-50" : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        {ico}
                      </button>
                    ))}
                    <label className="h-8 px-2.5 rounded-lg border border-gray-200 hover:border-gray-300 bg-white text-[10px] font-bold text-gray-500 flex items-center justify-center cursor-pointer transition select-none">
                      Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            toast.success("Logo uploaded successfully");
                            setForm({ ...form, icon: "📁" });
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Project Name */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Project Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    if (e.target.value) setErrors(prev => ({ ...prev, name: "" }));
                  }}
                  placeholder="Enter project name"
                  className={`w-full px-4 py-3 rounded-2xl border text-sm outline-none transition ${
                    errors.name ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15"
                  }`}
                  data-testid="new-project-name"
                />
                {errors.name && <p className="text-red-500 text-[10px] mt-1 font-semibold">{errors.name}</p>}
              </div>

              {/* One-line Tagline */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">One-line Tagline</label>
                <input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Elevator pitch or tagline"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none"
                  data-testid="new-project-tagline"
                />
              </div>

              {/* Industry */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Industry *</label>
                <select
                  value={form.industry}
                  onChange={(e) => {
                    setForm({ ...form, industry: e.target.value });
                    if (e.target.value) setErrors(prev => ({ ...prev, industry: "" }));
                  }}
                  className={`w-full px-4 py-3 rounded-2xl border text-sm outline-none bg-white transition ${
                    errors.industry ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15"
                  }`}
                  data-testid="new-project-industry"
                >
                  <option value="">Select Industry</option>
                  {[
                    "AI & Machine Learning", "SaaS", "FinTech", "EdTech", "HealthTech",
                    "E-commerce", "Cybersecurity", "ClimateTech", "Agriculture", "Logistics",
                    "Real Estate", "Gaming", "Creator Economy", "Web3", "Other"
                  ].map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
                {errors.industry && <p className="text-red-500 text-[10px] mt-1 font-semibold">{errors.industry}</p>}
              </div>

              {/* Startup Stage */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Startup Stage</label>
                <select
                  value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none bg-white"
                  data-testid="new-project-stage"
                >
                  {["Idea", "Validation", "MVP", "Beta", "Early Revenue", "Growth", "Scaling"].map((stg) => (
                    <option key={stg} value={stg}>{stg}</option>
                  ))}
                </select>
              </div>

              {/* Problem Statement */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Problem Statement *</label>
                <textarea
                  value={form.problem}
                  onChange={(e) => {
                    setForm({ ...form, problem: e.target.value });
                    if (e.target.value) setErrors(prev => ({ ...prev, problem: "" }));
                  }}
                  placeholder="What real-world problem are you solving?"
                  className={`w-full px-4 py-3 rounded-2xl border text-sm outline-none min-h-[70px] transition ${
                    errors.problem ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15"
                  }`}
                  data-testid="new-project-problem"
                />
                {errors.problem && <p className="text-red-500 text-[10px] mt-1 font-semibold">{errors.problem}</p>}
              </div>

              {/* Solution */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Solution *</label>
                <textarea
                  value={form.solution}
                  onChange={(e) => {
                    setForm({ ...form, solution: e.target.value });
                    if (e.target.value) setErrors(prev => ({ ...prev, solution: "" }));
                  }}
                  placeholder="Describe your product or solution."
                  className={`w-full px-4 py-3 rounded-2xl border text-sm outline-none min-h-[70px] transition ${
                    errors.solution ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15"
                  }`}
                  data-testid="new-project-solution"
                />
                {errors.solution && <p className="text-red-500 text-[10px] mt-1 font-semibold">{errors.solution}</p>}
              </div>

              {/* Target Audience */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Target Audience</label>
                <textarea
                  value={form.target_audience}
                  onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                  placeholder="Who are your ideal customers?"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none min-h-[60px]"
                  data-testid="new-project-audience"
                />
              </div>

              {/* Business Model */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Business Model</label>
                <select
                  value={form.business_model}
                  onChange={(e) => setForm({ ...form, business_model: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none bg-white font-medium"
                  data-testid="new-project-business-model"
                >
                  <option value="">Select Business Model</option>
                  {[
                    "B2B", "B2C", "D2C", "Marketplace", "Subscription", "SaaS",
                    "Freemium", "Enterprise", "Transaction Based", "Advertising", "Licensing"
                  ].map((bm) => (
                    <option key={bm} value={bm}>{bm}</option>
                  ))}
                </select>
              </div>

              {/* Revenue Model */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Revenue Model</label>
                <input
                  value={form.revenue_model}
                  onChange={(e) => setForm({ ...form, revenue_model: e.target.value })}
                  placeholder="Subscription, Commission, Ads, Licensing..."
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none"
                  data-testid="new-project-revenue-model"
                />
              </div>

              {/* Project Description */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Project Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => {
                    setForm({ ...form, description: e.target.value });
                    if (e.target.value) setErrors(prev => ({ ...prev, description: "" }));
                  }}
                  placeholder="Explain your startup idea in detail..."
                  className={`w-full px-4 py-3 rounded-2xl border text-sm outline-none min-h-[100px] transition ${
                    errors.description ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15"
                  }`}
                  data-testid="new-project-description"
                />
                {errors.description && <p className="text-red-500 text-[10px] mt-1 font-semibold">{errors.description}</p>}
              </div>

              {/* Current Goals */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-2">Current Goals</label>
                <div className="grid grid-cols-2 gap-2 bg-gray-50 p-3 rounded-2xl border border-gray-100">
                  {[
                    "Validate Idea", "Build MVP", "Market Research", "Find Co-founder",
                    "Raise Funding", "Acquire Customers", "Launch Product", "Hire Team", "Improve Branding"
                  ].map((goal) => {
                    const isChecked = form.goals.includes(goal);
                    return (
                      <label key={goal} className="flex items-center gap-2 text-[11px] text-gray-650 cursor-pointer font-semibold select-none">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const nextGoals = isChecked
                              ? form.goals.filter(g => g !== goal)
                              : [...form.goals, goal];
                            setForm({ ...form, goals: nextGoals });
                          }}
                          className="rounded border-gray-300 text-[#7C3AED] focus:ring-[#7C3AED]"
                        />
                        {goal}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Website */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Website</label>
                <input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none"
                  data-testid="new-project-website"
                />
              </div>

              {/* Visibility */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-2">Project Visibility</label>
                <div className="flex items-center gap-5 bg-gray-50 px-4 py-3 rounded-2xl border border-gray-100">
                  {["Private", "Team", "Public"].map((vis) => (
                    <label key={vis} className="flex items-center gap-1.5 text-xs text-gray-655 cursor-pointer font-bold select-none">
                      <input
                        type="radio"
                        name="visibility"
                        value={vis}
                        checked={form.visibility === vis}
                        onChange={() => setForm({ ...form, visibility: vis })}
                        className="text-[#7C3AED] focus:ring-[#7C3AED]"
                      />
                      {vis}
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-2">Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {form.tags.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 text-[10px] bg-[#F4F1FF] border border-[#7C3AED]/10 text-[#7C3AED] font-bold px-2.5 py-1 rounded-full">
                      {t}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tags: form.tags.filter(tag => tag !== t) })}
                        className="hover:text-red-500 font-extrabold text-[12px] ml-0.5 line-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["AI", "Startup", "SaaS", "Education", "Healthcare", "Finance", "Automation"].map((t) => {
                    const isSelected = form.tags.includes(t);
                    if (isSelected) return null;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm({ ...form, tags: [...form.tags, t] })}
                        className="text-[9px] font-bold text-gray-500 hover:text-[#7C3AED] bg-gray-50 border border-gray-100 hover:border-[#7C3AED]/20 px-2.5 py-1 rounded-lg transition"
                      >
                        + {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Team Size */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Team Size</label>
                <select
                  value={form.team_size}
                  onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none bg-white font-medium"
                  data-testid="new-project-team-size"
                >
                  {["Solo Founder", "2–5 Members", "6–10 Members", "10+"].map((sz) => (
                    <option key={sz} value={sz}>{sz}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit" disabled={busy}
              className="glow-button w-full rounded-full py-3.5 text-sm font-semibold disabled:opacity-70 mt-1 shrink-0"
              data-testid="new-project-submit"
            >
              {busy ? "Creating…" : "Create project"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
